import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import MembershipPage from "./pages/MembershipPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import BusinessDirectoryPage from "./pages/BusinessDirectoryPage";
import BusinessDetailPage from "./pages/BusinessDetailPage";
import BusinessMapPage from "./pages/BusinessMapPage";
import AffiliateDashboardPage from "./pages/AffiliateDashboardPage";
import AdminPage from "./pages/AdminPage";
import ScannerPage from "./pages/ScannerPage";
import BusinessSignupPage from "./pages/BusinessSignupPage";
import AffiliateSignupPage from "./pages/AffiliateSignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import LocationPage from "./pages/LocationPage";
import TeamInvitePage from "./pages/TeamInvitePage";
import ValidatePage from "./pages/ValidatePage";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow admin, team_member, and territory_owner roles for admin panel
  if (adminOnly && user.role !== "admin" && user.role !== "team_member" && user.role !== "territory_owner") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Redirect admin/territory_owner from member dashboard to admin panel
const DashboardRedirect = ({ children }) => {
  const { user } = useAuth();
  if (user && (user.role === "admin" || user.role === "team_member" || user.role === "territory_owner")) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardRedirect><DashboardPage /></DashboardRedirect></ProtectedRoute>
            } />
            <Route path="/membership" element={
              <ProtectedRoute><MembershipPage /></ProtectedRoute>
            } />
            <Route path="/payment/success" element={
              <ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>
            } />
            <Route path="/businesses" element={<BusinessDirectoryPage />} />
            <Route path="/businesses/map" element={<BusinessMapPage />} />
            <Route path="/businesses/:id" element={<BusinessDetailPage />} />
            <Route path="/affiliate/:code" element={<AffiliateDashboardPage />} />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>
            } />
            <Route path="/scan" element={<ScannerPage />} />
            <Route path="/scan/pair/:token" element={<ScannerPage />} />
            <Route path="/validate/:membershipId/:userId" element={<ValidatePage />} />
            <Route path="/validate/:code" element={<ValidatePage />} />
            <Route path="/validate" element={<ValidatePage />} />
            <Route path="/partner-signup" element={<BusinessSignupPage />} />
            <Route path="/affiliate-signup" element={<AffiliateSignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/discounts/:slug" element={<LocationPage />} />
            <Route path="/team-invite" element={<TeamInvitePage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
